import React from 'react';
import { ActivityIcon, GlobeIcon, LockIcon, PlusIcon } from 'lucide-react';
import type { HealthStatus } from '../../types/monitor';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/StatusBadge';
import { QuotaMeter } from '../../components/QuotaMeter';
import { EmptyState } from '../../components/EmptyState';
import { WatchtowerMark } from '../../components/WatchtowerMark';
import { Wordmark } from '../../components/Wordmark';
import { CertSeal } from '../../components/brand/CertSeal';
import { SignalArc } from '../../components/brand/SignalArc';
import { Beacon } from '../../components/brand/Beacon';
import { DarkTower } from '../../components/brand/DarkTower';
import { ArcQuota } from '../../components/brand/ArcQuota';
import { markVariants } from '../../components/brand/markVariants';
const markSizes = [{
  size: 'h-4 w-4',
  label: '16'
}, {
  size: 'h-5 w-5',
  label: '20'
}, {
  size: 'h-6 w-6',
  label: '24'
}, {
  size: 'h-8 w-8',
  label: '32'
}, {
  size: 'h-12 w-12',
  label: '48'
}];
const statuses: HealthStatus[] = ['healthy', 'warning', 'critical', 'paused'];
const statusTone: Record<HealthStatus, string> = {
  healthy: 'bg-healthy-soft text-healthy-ink border-healthy-border',
  warning: 'bg-warn-soft text-warn-ink border-warn-border',
  critical: 'bg-critical-soft text-critical-ink border-critical-border',
  paused: 'bg-paused-soft text-paused-ink border-paused-border'
};
const statusLabel: Record<HealthStatus, string> = {
  healthy: 'Healthy',
  warning: 'Warning',
  critical: 'Critical',
  paused: 'Not monitored'
};
const senders = [{
  address: 'alerts@watchtowerworks.com',
  use: 'Expiry warnings, downtime, recovery'
}, {
  address: 'billing@watchtowerworks.com',
  use: 'Receipts, card failures, plan changes'
}, {
  address: 'status@watchtowerworks.com',
  use: 'Our own incidents and maintenance'
}];
export function Brand() {
  return <>
      <section className="bg-shell" data-id="element-1250" data-mp="2vhiojalsvlls">
        <div className="mx-auto w-full max-w-page px-5 py-14 sm:px-8" data-id="element-1251" data-mp="2rgffp8266pu2">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-brand-bright" data-id="element-1252" data-mp="2rijr0b2eer4d">
            specimen · not applied
          </p>
          <h1 className="mt-5 max-w-3xl text-[2.2rem] font-semibold leading-[1.08] tracking-[-0.02em] text-white sm:text-[2.8rem]" data-id="element-1253" data-mp="2hr5kvkfxyt29">
            Proposed icons and branding for Watchtower
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/65" data-id="element-1254" data-mp="27ry6ctxh0sdk">
            Everything below is rendered for review only. Start with the eight
            mark directions — tell me a letter and I will run it through the
            header, sidebar, emails, invoices and favicon.
          </p>

          <div className="mt-8 flex flex-wrap gap-x-7 gap-y-5" data-id="element-1255" data-mp="1zrhfml6lptw">
            {markVariants.map(variant => <span key={variant.id} className="text-center" data-id="element-1256" data-mp="3t6u5441t9dzd">
                <variant.Mark className="mx-auto h-8 w-8 text-brand-bright" data-id="element-1257" data-mp="4k7a7ej3thek" />
                <span className="mt-2 block font-mono text-[11px] text-white/45" data-id="element-1258" data-mp="4i3914clawhz">
                  {variant.id}
                </span>
              </span>)}
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-page gap-5 px-5 py-12 sm:px-8" data-id="element-1259" data-mp="j8tyw6umflig">
        <Panel as="section" className="border-brand-border" data-id="element-1260" data-mp="jaws0zr2lure">
          <PanelHeader title="0 · Eight directions for the mark" description="Pick one and I will run it through the whole product. Each is shown large, then at 16 / 20 / 24px, then reversed on navy, then locked up with the wordmark." level={2} data-id="element-1261" data-mp="3hrr6iev568uf" />
          <div className="grid gap-px bg-line sm:grid-cols-2 xl:grid-cols-4" data-id="element-1262" data-mp="3vra082n7hhn7">
            {markVariants.map(variant => <article key={variant.id} className="bg-surface" data-id="element-1263" data-mp="16y4ul9x46ppq">
                <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5" data-id="element-1264" data-mp="1az86xmfye3m7">
                  <span className="font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-brand" data-id="element-1265" data-mp="19jvvfdvimb7b">
                    {variant.id}
                  </span>
                  <span className="text-[13px] font-semibold text-ink" data-id="element-1266" data-mp="1jgqrzr88a1iy">
                    {variant.name}
                  </span>
                </div>

                <div className="flex h-[128px] items-center justify-center bg-canvas" data-id="element-1267" data-mp="1egm6tnwdkime">
                  <variant.Mark className="h-16 w-16 text-brand" data-id="element-1268" data-mp="1sg412isfwo35" />
                </div>

                <div className="flex items-end justify-center gap-5 border-y border-line px-4 py-4" data-id="element-1269" data-mp="1nhu545yw5jgh">
                  <variant.Mark className="h-4 w-4 text-ink" data-id="element-1270" data-mp="2266siejwxtzd" />
                  <variant.Mark className="h-5 w-5 text-ink" data-id="element-1271" data-mp="1x8ou3i3iux5m" />
                  <variant.Mark className="h-6 w-6 text-ink" data-id="element-1272" data-mp="1xai14yi1rje5" />
                </div>

                <div className="flex h-[92px] items-center justify-center gap-4 bg-shell" data-id="element-1273" data-mp="2b8ppwj4xjee8">
                  <variant.Mark className="h-9 w-9 text-brand-bright" data-id="element-1274" data-mp="2785w9pjkeswu" />
                  <variant.Mark className="h-5 w-5 text-white" data-id="element-1275" data-mp="2pun4q574he94" />
                </div>

                <div className="flex items-center gap-2.5 border-t border-line px-4 py-3.5" data-id="element-1276" data-mp="2uuhweayq27v8">
                  <variant.Mark className="h-7 w-7 shrink-0 text-brand" data-id="element-1277" data-mp="2gugtyr2t6sdn" />
                  <span className="font-mono text-[13px] font-medium uppercase tracking-[0.14em] text-ink" data-id="element-1278" data-mp="2gwjjla01ujhu">
                    Watchtower
                  </span>
                </div>

                <p className="border-t border-line bg-canvas px-4 py-3 text-xs leading-relaxed text-ink-muted" data-id="element-1279" data-mp="2732n1y8j2qah">
                  {variant.note}
                </p>
              </article>)}
          </div>
        </Panel>

        <Panel as="section" data-id="element-1280" data-mp="225173m6ef738">
          <PanelHeader title="1 · The mark at size" description="Lighthouse silhouette throwing a lit beam. The beam drops away at 16px, where it stops resolving." level={2} data-id="element-1281" data-mp="226cbno7o4ive" />
          <div className="grid gap-px bg-line sm:grid-cols-2" data-id="element-1282" data-mp="1t433v63sb3lp">
            <div className="flex flex-wrap items-end gap-8 bg-surface p-6" data-id="element-1283" data-mp="3uu1vo4rgheqd">
              {markSizes.map(item => <span key={item.label} className="text-center" data-id="element-1284" data-mp="3q2a35ooqoyxl">
                  <WatchtowerMark beam={item.label !== '16'} className={`${item.size} text-brand`} data-id="element-1285" data-mp="3m18a5plf1j42" />
                  <span className="mt-2 block font-mono text-[10px] text-ink-soft" data-id="element-1286" data-mp="3h483pdknmecf">
                    {item.label}px
                  </span>
                </span>)}
            </div>
            <div className="flex flex-wrap items-end gap-8 bg-shell p-6" data-id="element-1287" data-mp="dmgbby2lvsdp">
              {markSizes.map(item => <span key={item.label} className="text-center" data-id="element-1288" data-mp="8oer39tg1n0n">
                  <WatchtowerMark beam={item.label !== '16'} className={`${item.size} text-white`} data-id="element-1289" data-mp="3mz3pb3sn4f3" />
                  <span className="mt-2 block font-mono text-[10px] text-white/40" data-id="element-1290" data-mp="2od6o5bd1ugsd">
                    {item.label}px
                  </span>
                </span>)}
            </div>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1291" data-mp="2j9nqgukkxtq2">
          <PanelHeader title="2 · Wordmark lockups" description="The approved lockup at three sizes, on light and on the shell." level={2} data-id="element-1292" data-mp="2j6rrr40wr10k" />
          <div className="grid gap-px bg-line md:grid-cols-3" data-id="element-1293" data-mp="2t006c9hg8ypv">
            {[{
            size: 'sm' as const,
            subline: true,
            name: 'Small · headers, sidebars',
            note: 'The default. Mark at 28px with the WORKS line intact.'
          }, {
            size: 'md' as const,
            subline: true,
            name: 'Medium · auth, invoices',
            note: 'For quieter pages that can carry a larger lockup.'
          }, {
            size: 'sm' as const,
            subline: false,
            name: 'Compact · tight bars',
            note: 'WORKS dropped where vertical space runs out.'
          }].map(option => <div key={option.name} className="bg-surface" data-id="element-1294" data-mp="3t7dqv3raq2p0">
                <div className="flex h-[104px] items-center justify-center border-b border-line bg-canvas px-5" data-id="element-1295" data-mp="3t8osawx9qycy">
                  <Wordmark size={option.size} showSubline={option.subline} tone="light" data-id="element-1296" data-mp="30i6sq5vr7f4a" />
                </div>
                <div className="flex h-[104px] items-center justify-center border-b border-line bg-shell px-5" data-id="element-1297" data-mp="2vlg8r7gtvzdl">
                  <Wordmark size={option.size} showSubline={option.subline} tone="dark" data-id="element-1298" data-mp="35hjd8jz5ka3p" />
                </div>
                <div className="px-5 py-4" data-id="element-1299" data-mp="kr2f9vm1gj1m">
                  <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1300" data-mp="kq0taw3hic54">
                    {option.name}
                  </p>
                  <p className="mt-1.5 text-[13px] text-ink-muted" data-id="element-1301" data-mp="x9o962xy1cq3">
                    {option.note}
                  </p>
                </div>
              </div>)}
          </div>
        </Panel>

        <Panel as="section" data-id="element-1302" data-mp="nb8gvwn6jxf0">
          <PanelHeader title="2b · App icon and favicon" description="The tower alone, no wordmark. Centred in the tile with the beam clearing the right edge." level={2} data-id="element-1303" data-mp="ncjlfyog9976" />
          <div className="flex flex-wrap items-end gap-8 px-5 py-6 sm:px-6" data-id="element-1304" data-mp="1fzmhmk5nl7u9">
            {([{
            tile: 'bg-brand',
            mark: 'text-white',
            label: '512 · brand'
          }, {
            tile: 'bg-shell',
            mark: 'text-white',
            label: '512 · shell'
          }, {
            tile: 'bg-canvas border border-line',
            mark: 'text-brand',
            label: '512 · light'
          }] as const).map(option => <span key={option.label} className="text-center" data-id="element-1305" data-mp="16lefu9opypgy">
                <span className={`flex h-20 w-20 items-center justify-center rounded-[18px] ${option.tile}`} data-id="element-1306" data-mp="1amzwm64djnwf">
                  <WatchtowerMark className={`h-12 w-12 ${option.mark}`} data-id="element-1307" data-mp="wmgv7huezg38" />
                </span>
                <span className="mt-2.5 block font-mono text-[10px] text-ink-soft" data-id="element-1308" data-mp="11jyuegimqgy1">
                  {option.label}
                </span>
              </span>)}

            <span className="text-center" data-id="element-1309" data-mp="ronlwfxte5yq">
              <span className="flex h-20 w-20 items-center justify-center rounded-[18px] bg-brand" data-id="element-1310" data-mp="rpgp56xkedc4">
                <WatchtowerMark beam={false} className="h-12 w-12 text-white" data-id="element-1311" data-mp="due9vt6tvbmn" />
              </span>
              <span className="mt-2.5 block font-mono text-[10px] text-ink-soft" data-id="element-1312" data-mp="isxunq9oqhdf">
                32 · no beam
              </span>
            </span>

            <span className="flex items-end gap-4 pb-6" data-id="element-1313" data-mp="8w31zo03fb72">
              <WatchtowerMark className="h-8 w-8 text-brand" data-id="element-1314" data-mp="3lgqhbuj94zdf" />
              <WatchtowerMark className="h-6 w-6 text-brand" data-id="element-1315" data-mp="3lh0crxn2fyqi" />
              <WatchtowerMark beam={false} className="h-4 w-4 text-brand" data-id="element-1316" data-mp="3hfwyvgokwcrx" />
            </span>
          </div>
          <p className="border-t border-line bg-canvas px-5 py-3.5 text-[13px] leading-relaxed text-ink-muted sm:px-6" data-id="element-1317" data-mp="3vfxuohpzku0j">
            Use the brand-green tile as the primary app icon and the beamless
            version at 32px and below. I cannot install a favicon from here —
            upload the exported square through the{' '}
            <strong data-id="element-1318" data-mp="46iqlrj8z3k4">PUBLISH</strong> button in the top right.
          </p>
        </Panel>

        <Panel as="section" data-id="element-1319" data-mp="hz8drzaiieit">
          <PanelHeader title="3 · Service glyphs" description="Custom replacements for the two Lucide icons carrying the most meaning in the product." level={2} data-id="element-1320" data-mp="d0x2rl8hdxz0" />
          <div className="grid gap-px bg-line sm:grid-cols-2" data-id="element-1321" data-mp="mzcv1rj8vda3">
            <div className="bg-surface p-6" data-id="element-1322" data-mp="n0xplh5k6w7y">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1323" data-mp="u2t4m776q6pq">
                Certificate expiry
              </p>
              <div className="mt-5 flex items-end gap-8" data-id="element-1324" data-mp="z1nn1818pt9b">
                <span className="text-center" data-id="element-1325" data-mp="14079rannr92q">
                  <LockIcon className="mx-auto h-8 w-8 text-brand" aria-hidden="true" data-id="element-1326" data-mp="13ye1jqxecgsm" />
                  <span className="mt-3 block text-xs text-ink-soft" data-id="element-1327" data-mp="1dqtkorkfjbf3">
                    current
                  </span>
                </span>
                <span className="text-center" data-id="element-1328" data-mp="1hq2dnu7nb59l">
                  <CertSeal className="mx-auto h-8 w-8 text-brand" data-id="element-1329" data-mp="1mqgn9gowan9q" />
                  <span className="mt-3 block text-xs font-medium text-ink" data-id="element-1330" data-mp="1mp5jvhzd3hj5">
                    CertSeal
                  </span>
                </span>
                <span className="flex items-end gap-3 pb-6" data-id="element-1331" data-mp="3ojd63pp1qu4e">
                  <CertSeal className="h-4 w-4 text-ink-muted" data-id="element-1332" data-mp="3thelo0n9kbb4" />
                  <CertSeal className="h-5 w-5 text-ink-muted" data-id="element-1333" data-mp="3thofjzbfj2m3" />
                  <CertSeal className="h-6 w-6 text-ink-muted" data-id="element-1334" data-mp="3lyypnr8820d6" />
                </span>
              </div>
              <p className="mt-5 text-[13px] leading-relaxed text-ink-muted" data-id="element-1335" data-mp="3h65i6vldcdxe">
                A stamped seal with the beacon lit at its centre — a certificate
                is an attestation, and this reads as one.
              </p>
            </div>

            <div className="bg-surface p-6" data-id="element-1336" data-mp="8rdfgszfu31r">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1337" data-mp="8qc0wej7qyfa">
                Uptime
              </p>
              <div className="mt-5 flex items-end gap-8" data-id="element-1338" data-mp="3q7keoib23p2">
                <span className="text-center" data-id="element-1339" data-mp="3uxk69gu4v5bb">
                  <ActivityIcon className="mx-auto h-8 w-8 text-brand" aria-hidden="true" data-id="element-1340" data-mp="3v0eor2tl6g23" />
                  <span className="mt-3 block text-xs text-ink-soft" data-id="element-1341" data-mp="2jkhmkcwqlzmx">
                    current
                  </span>
                </span>
                <span className="text-center" data-id="element-1342" data-mp="2emojhsqqdlqv">
                  <SignalArc className="mx-auto h-8 w-8 text-brand" data-id="element-1343" data-mp="78vejw5qvqka" />
                  <span className="mt-3 block text-xs font-medium text-ink" data-id="element-1344" data-mp="bzlgezeypba8">
                    SignalArc
                  </span>
                </span>
                <span className="flex items-end gap-3 pb-6" data-id="element-1345" data-mp="3thwmo2o5pxj1">
                  <SignalArc className="h-4 w-4 text-ink-muted" data-id="element-1346" data-mp="29in0y3xhvbd" />
                  <SignalArc className="h-5 w-5 text-ink-muted" data-id="element-1347" data-mp="27pgditbfb3d" />
                  <SignalArc className="h-6 w-6 text-ink-muted" data-id="element-1348" data-mp="q060s00izf5u" />
                </span>
              </div>
              <p className="mt-5 text-[13px] leading-relaxed text-ink-muted" data-id="element-1349" data-mp="uxy37nysqjoa">
                The mark&apos;s arc repeated outward from a transmitting point.
                Reads as pinging rather than as a generic pulse line.
              </p>
            </div>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1350" data-mp="30q4jy80xmfwv">
          <PanelHeader title="4 · Status glyphs — the risky one" description="Beacon signal strength instead of check / triangle / octagon. More ownable, but less instantly recognisable. Compare carefully." level={2} data-id="element-1351" data-mp="30u0pyxyl85m9" />
          <div className="space-y-6 px-5 py-6 sm:px-6" data-id="element-1352" data-mp="1itj8yrpudep8">
            <div data-id="element-1353" data-mp="1wuc6zmfcqrx3">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1354" data-mp="1wsspxtlvgcu9">
                Current — universal shapes
              </p>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-1355" data-mp="1sqe89ctxvj4k">
                {statuses.map(status => <StatusBadge key={status} status={status} size="md" data-id="element-1356" data-mp="103jpckcrsrt7" />)}
              </div>
            </div>

            <div data-id="element-1357" data-mp="1dvhny18uv5b4">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1358" data-mp="1dvzt5ndhwtv6">
                Proposed — beacon signal
              </p>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-1359" data-mp="lenkew60fuwt">
                {statuses.map(status => <span key={status} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${statusTone[status]}`} data-id="element-1360" data-mp="hd222vapiof8">
                    <Beacon status={status} className="h-3.5 w-3.5" data-id="element-1361" data-mp="2lgesb6jlqozx" />
                    {statusLabel[status]}
                  </span>)}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-6 rounded-card border border-line bg-canvas px-5 py-4" data-id="element-1362" data-mp="2v6q4thhcq6if">
                {statuses.map(status => <span key={status} className="text-center" data-id="element-1363" data-mp="1xksh95o47azm">
                    <Beacon status={status} className={`mx-auto h-7 w-7 ${status === 'healthy' ? 'text-healthy' : status === 'warning' ? 'text-warn' : status === 'critical' ? 'text-critical' : 'text-paused'}`} data-id="element-1364" data-mp="22h96y4aypxet" />
                    <span className="mt-2 block font-mono text-[10px] text-ink-soft" data-id="element-1365" data-mp="1c8bf3hfskara">
                      {status}
                    </span>
                  </span>)}
              </div>
            </div>

            <p className="max-w-2xl text-[13px] leading-relaxed text-ink-muted" data-id="element-1366" data-mp="12gxjvluvhx3y">
              My read: adopt these on the marketing pages and the monitor detail
              header, but keep the universal shapes in the dense monitor list
              and alert log, where people scan for a check or a cross.
            </p>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1367" data-mp="jj4hcko5vp73">
          <PanelHeader title="5 · The arc as a device" description="The quota meter is the most-seen component in the app. Same information, drawn with the brand's geometry." level={2} data-id="element-1368" data-mp="jkfjkg1ykow3" />
          <div className="grid gap-px bg-line lg:grid-cols-3" data-id="element-1369" data-mp="2dwm5o9we4q36">
            <div className="bg-surface p-6" data-id="element-1370" data-mp="28zcaedmblk7f">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1371" data-mp="28whqcn77y1ej">
                Current — linear
              </p>
              <div className="mt-4" data-id="element-1372" data-mp="1msb842wvrvk5">
                <QuotaMeter used={9} limit={15} planName="Pro" showUpgrade={false} data-id="element-1373" data-mp="1mr9srm8u0mwm" />
              </div>
            </div>
            <div className="flex flex-col items-center justify-center bg-surface p-6" data-id="element-1374" data-mp="1d05e6nn2j42b">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1375" data-mp="z15pxh3gwr6o">
                Proposed — arc, on light
              </p>
              <div className="mt-5" data-id="element-1376" data-mp="p4swmkbu4fcg">
                <ArcQuota used={9} limit={15} data-id="element-1377" data-mp="p5ucr37pjs11" />
              </div>
            </div>
            <div className="flex flex-col items-center justify-center bg-shell p-6" data-id="element-1378" data-mp="u45kn8efzsgm">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-white/40" data-id="element-1379" data-mp="2ji2cquyvyz3o">
                Proposed — arc, in sidebar
              </p>
              <div className="mt-5" data-id="element-1380" data-mp="2of15pa6td00a">
                <ArcQuota used={9} limit={15} tone="dark" data-id="element-1381" data-mp="cta3xtwpyeu3" />
              </div>
            </div>
          </div>
          <p className="border-t border-line px-5 py-4 text-[13px] leading-relaxed text-ink-muted sm:px-6" data-id="element-1382" data-mp="7w06ps338yvp">
            Tradeoff: the arc is more distinctive but the linear bar states the
            remainder more plainly, and it carries the &ldquo;3 left on
            Pro&rdquo; sentence underneath. A hybrid works — arc in the sidebar
            where space is tight, linear bar on the add-monitor form and billing
            page where the explanation matters.
          </p>
        </Panel>

        <Panel as="section" data-id="element-1383" data-mp="15ho1k8jwb954">
          <PanelHeader title="6 · Empty states" description="The one place an illustration earns its keep, because there is no data to look at." level={2} data-id="element-1384" data-mp="15hxspzgofmce" />
          <div className="grid gap-px bg-line lg:grid-cols-2" data-id="element-1385" data-mp="37ac6cmc6u4n0">
            <div className="bg-surface" data-id="element-1386" data-mp="32c10snsxegal">
              <p className="border-b border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1387" data-mp="32d3rovq90iqi">
                Current
              </p>
              <EmptyState icon={GlobeIcon} title="No domains yet" body="Add the first site you would hate to find broken. You can switch on certificate expiry, uptime, or both." action={<Button data-id="element-1389" data-mp="evkglvbccd77">
                    <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1390" data-mp="euspoxsov3n9" />
                    Add a domain
                  </Button>} data-id="element-1388" data-mp="ts1t4pv9nxvc" />
            </div>
            <div className="bg-surface" data-id="element-1391" data-mp="otgv8x3hlqa2">
              <p className="border-b border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1392" data-mp="oujm550t7spz">
                Proposed
              </p>
              <div className="flex flex-col items-center px-6 py-10 text-center" data-id="element-1393" data-mp="208dt8whnfptk">
                <DarkTower className="h-20 w-32 text-ink-soft" data-id="element-1394" data-mp="1va2lqsettrei" />
                <h3 className="mt-5 text-sm font-semibold text-ink" data-id="element-1395" data-mp="1v9kivcxnsexm">
                  Nothing on watch yet
                </h3>
                <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-ink-muted" data-id="element-1396" data-mp="3i6ilo6q4t84k">
                  The beacon is unlit. Add the first site you would hate to find
                  broken, and the watch starts on the next sweep.
                </p>
                <div className="mt-5" data-id="element-1397" data-mp="396dn8m2x1k2j">
                  <Button data-id="element-1398" data-mp="q0nmn777e5v">
                    <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1399" data-mp="pscp0umyixb" />
                    Add a domain
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1400" data-mp="3n4twajo93mnu">
          <PanelHeader title="7 · Motion signature" description="One rhythm for everything that means live — 1.8s, linear, already used by the ticker badge and the runway header." level={2} data-id="element-1401" data-mp="3n3iqyff5q6um" />
          <div className="grid gap-px bg-line sm:grid-cols-2" data-id="element-1402" data-mp="og4tz2brts0k">
            <div className="flex flex-wrap items-center gap-6 bg-surface p-6" data-id="element-1403" data-mp="ohfw6xpkirpk">
              {([{
              color: 'bg-healthy',
              label: 'checking'
            }, {
              color: 'bg-brand',
              label: 'live'
            }, {
              color: 'bg-warn',
              label: 'retrying'
            }] as const).map(item => <span key={item.label} className="flex items-center gap-2.5" data-id="element-1404" data-mp="jdayxsqlg4v0">
                  <span className={`pulse-dot h-2 w-2 rounded-full ${item.color}`} data-id="element-1405" data-mp="fd97sdihittz" />
                  <span className="font-mono text-[11px] text-ink-muted" data-id="element-1406" data-mp="akzhgwdgjrr6">
                    {item.label}
                  </span>
                </span>)}
            </div>
            <div className="bg-shell p-6" data-id="element-1407" data-mp="omibiz58lp1">
              <div className="flex items-center gap-2.5" data-id="element-1408" data-mp="lqi1nztpkxf">
                <span className="pulse-dot h-2 w-2 rounded-full bg-brand-bright" data-id="element-1409" data-mp="3rt30058j62ee" />
                <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-white/60" data-id="element-1410" data-mp="3ns10roem5qcf">
                  the watch is running
                </span>
              </div>
              <p className="mt-3 text-[13px] leading-relaxed text-white/55" data-id="element-1411" data-mp="27c3kmcghdk3t">
                Also proposed: replace the &ldquo;Check now&rdquo; spinner with a
                single 240ms arc sweep, so the product&apos;s one manual action
                uses the brand&apos;s own gesture.
              </p>
            </div>
          </div>
        </Panel>

        <Panel as="section" data-id="element-1412" data-mp="1jo3vti2qc3rv">
          <PanelHeader title="8 · Brand surfaces" description="Where the identity shows up outside the interface." level={2} data-id="element-1413" data-mp="1jkogce558spx" />
          <div className="grid gap-px bg-line lg:grid-cols-2" data-id="element-1414" data-mp="1tj43y74ypjuo">
            <div className="bg-surface p-6" data-id="element-1415" data-mp="1yboxr8nvbwy7">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1416" data-mp="1ydi6qulyet9d">
                Sender identities
              </p>
              <ul className="mt-4 divide-y divide-line" data-id="element-1417" data-mp="15ukwfttqlb3n">
                {senders.map(sender => <li key={sender.address} className="py-3" data-id="element-1418" data-mp="37jigoxzlphvc">
                    <p className="font-mono text-[13px] text-ink" data-id="element-1419" data-mp="37hxld65gpuwf">
                      {sender.address}
                    </p>
                    <p className="mt-1 text-xs text-ink-muted" data-id="element-1420" data-mp="3hamwu110j8rr">{sender.use}</p>
                  </li>)}
              </ul>
            </div>

            <div className="bg-surface p-6" data-id="element-1421" data-mp="3cha7fooiexxy">
              <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1422" data-mp="3r7rhfwf7mw6g">
                Invoice letterhead
              </p>
              <div className="mt-4 rounded-card border border-line bg-canvas p-5" data-id="element-1423" data-mp="3m8wssdukadef">
                <div className="flex items-start justify-between gap-4 border-b border-line pb-4" data-id="element-1424" data-mp="429ygd0yog03">
                  <Wordmark size="md" tone="light" data-id="element-1425" data-mp="3vae47ix0xqws" />
                  <span className="text-right" data-id="element-1426" data-mp="25uoet7nuxcee">
                    <span className="block font-mono text-[13px] font-medium text-ink" data-id="element-1427" data-mp="25vgbklt6xg69">
                      WT-2041
                    </span>
                    <span className="mt-1 block font-mono text-[11px] text-ink-soft" data-id="element-1428" data-mp="ge2o5azjhnxn">
                      paid · 1 Sep 2026
                    </span>
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-4 pt-4" data-id="element-1429" data-mp="7lhauaeog9g7">
                  <span className="text-[13px] text-ink-muted" data-id="element-1430" data-mp="7joafcunqlgn">
                    Pro · 15 services · monthly
                  </span>
                  <span className="font-mono text-lg font-medium text-ink" data-id="element-1431" data-mp="3tsfsjvs0yl9p">
                    $15.00
                  </span>
                </div>
              </div>
              <p className="mt-4 text-[13px] leading-relaxed text-ink-muted" data-id="element-1432" data-mp="2k1p0g4odywr">
                Also needed: a 512px square export of the mark for the favicon.
                I can produce it, but uploading it is done through the PUBLISH
                button in the top right — I cannot set a favicon from here.
              </p>
            </div>
          </div>
        </Panel>

        <Panel as="section" className="border-brand-border bg-brand-soft" data-id="element-1433" data-mp="1n1g98tyhsr39">
          <div className="px-5 py-5 sm:px-6" data-id="element-1434" data-mp="2vzzbf8jlxvd2">
            <h2 className="text-sm font-semibold text-brand-ink" data-id="element-1435" data-mp="2r038zdqk6bzm">
              If you only want a subset
            </h2>
            <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-brand-ink/85" data-id="element-1436" data-mp="2mxzx4nzz95dt">
              Lowest risk, highest return: the <strong data-id="element-1437" data-mp="2i0q7p8cl8tpz">mono wordmark (B)</strong>
              , <strong data-id="element-1438" data-mp="3epo2uro92tno">CertSeal + SignalArc</strong>, and the{' '}
              <strong data-id="element-1439" data-mp="3ep5vayw50t0g">DarkTower empty state</strong>. The beacon status set and
              the arc quota gauge change how people read data, so they are worth
              a second look before committing.
            </p>
          </div>
        </Panel>
      </div>
    </>;
}